import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@medikei/ui';
import { Flag, ToggleLeft, ToggleRight, Plus, Trash2, Save } from 'lucide-react';

interface FeatureFlag {
  id: string;
  key: string;
  enabled: boolean;
  rolloutPercentage: number | null;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

interface FeatureFlagsResponse {
  data: FeatureFlag[];
}

export function AdminFeatureFlagsPage() {
  const queryClient = useQueryClient();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState({
    key: '',
    enabled: false,
    rolloutPercentage: '',
    description: '',
  });

  const { data, isLoading } = useQuery<FeatureFlagsResponse>({
    queryKey: ['admin', 'feature-flags'],
    queryFn: async () => {
      const res = await fetch('/api/v1/admin/feature-flags', {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      });
      if (!res.ok) throw new Error('Failed to fetch feature flags');
      return res.json();
    },
  });

  const updateFlagMutation = useMutation({
    mutationFn: async ({ key, ...updates }: Partial<FeatureFlag> & { key: string }) => {
      const res = await fetch(`/api/v1/admin/feature-flags/${key}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error('Failed to update feature flag');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'feature-flags'] });
    },
  });

  const deleteFlagMutation = useMutation({
    mutationFn: async (key: string) => {
      const res = await fetch(`/api/v1/admin/feature-flags/${key}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      });
      if (!res.ok) throw new Error('Failed to delete feature flag');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'feature-flags'] });
    },
  });

  const createFlagMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/v1/admin/feature-flags', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({
          key: createForm.key,
          enabled: createForm.enabled,
          rolloutPercentage: createForm.rolloutPercentage ? parseInt(createForm.rolloutPercentage, 10) : undefined,
          description: createForm.description || undefined,
        }),
      });
      if (!res.ok) throw new Error('Failed to create feature flag');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'feature-flags'] });
      setShowCreateForm(false);
      setCreateForm({ key: '', enabled: false, rolloutPercentage: '', description: '' });
    },
  });

  const handleToggle = (flag: FeatureFlag) => {
    updateFlagMutation.mutate({ key: flag.key, enabled: !flag.enabled });
  };

  const handleRolloutChange = (flag: FeatureFlag, percentage: number) => {
    updateFlagMutation.mutate({ key: flag.key, rolloutPercentage: percentage });
  };

  const handleDelete = (key: string) => {
    if (confirm('Tem certeza que deseja excluir esta flag?')) {
      deleteFlagMutation.mutate(key);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Flag className="h-8 w-8 text-blue-600" />
          <h1 className="text-2xl font-bold">Feature Flags</h1>
        </div>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Nova Flag
        </button>
      </div>

      {showCreateForm && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Criar Nova Feature Flag</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Key</label>
                <input
                  type="text"
                  value={createForm.key}
                  onChange={(e) => setCreateForm({ ...createForm, key: e.target.value })}
                  placeholder="ex: NEW_EXPRESS_ALGORITHM"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rollout Percentage</label>
                <input
                  type="number"
                  value={createForm.rolloutPercentage}
                  onChange={(e) => setCreateForm({ ...createForm, rolloutPercentage: e.target.value })}
                  placeholder="0-100"
                  min="0"
                  max="100"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={createForm.description}
                  onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                  placeholder="Descrição da feature flag"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="flex items-center gap-4 mt-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={createForm.enabled}
                  onChange={(e) => setCreateForm({ ...createForm, enabled: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Habilitada por padrão</span>
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setShowCreateForm(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancelar
              </button>
              <button
                onClick={() => createFlagMutation.mutate()}
                disabled={!createForm.key}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                Criar
              </button>
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : (
        <div className="grid gap-4">
          {data?.data?.map((flag) => (
            <Card key={flag.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => handleToggle(flag)}
                      className={`${flag.enabled ? 'text-green-600' : 'text-gray-400'}`}
                    >
                      {flag.enabled ? (
                        <ToggleRight className="h-8 w-8" />
                      ) : (
                        <ToggleLeft className="h-8 w-8" />
                      )}
                    </button>
                    <div>
                      <h3 className="font-semibold text-lg">{flag.key}</h3>
                      {flag.description && (
                        <p className="text-sm text-gray-500">{flag.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-600">Rollout:</label>
                      <input
                        type="number"
                        value={flag.rolloutPercentage ?? 0}
                        onChange={(e) => handleRolloutChange(flag, parseInt(e.target.value, 10) || 0)}
                        min="0"
                        max="100"
                        className="w-16 px-2 py-1 text-center border rounded"
                      />
                      <span className="text-sm text-gray-600">%</span>
                    </div>
                    <button
                      onClick={() => handleDelete(flag.key)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-4 text-xs text-gray-400">
                  <span>Criado em: {new Date(flag.createdAt).toLocaleDateString('pt-BR')}</span>
                  <span>Atualizado em: {new Date(flag.updatedAt).toLocaleDateString('pt-BR')}</span>
                </div>
              </CardContent>
            </Card>
          ))}
          {(!data?.data || data.data.length === 0) && (
            <Card>
              <CardContent className="p-8 text-center">
                <Flag className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Nenhuma feature flag configurada</p>
                <p className="text-sm text-gray-400 mt-1">Clique em "Nova Flag" para criar a primeira</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
